import type { BlendMode, PixelData } from 'ag-psd'
import { renderLayerPixels, type LayerNode } from './psd'

export type CompositeBoundsMode = 'selection' | 'document'
export type CompositeBackground = 'transparent' | 'white' | 'black'

export interface CompositeOptions {
  bounds: CompositeBoundsMode
  documentWidth: number
  documentHeight: number
  applyMask: boolean
  applyOpacity: boolean
  applyBlendModes: boolean
  background: CompositeBackground
}

export interface CompositeBounds {
  left: number
  top: number
  right: number
  bottom: number
  width: number
  height: number
}

export interface CompositeResult {
  image: PixelData
  bounds: CompositeBounds
  approximatedBlendModes: BlendMode[]
}

const SUPPORTED_BLEND_MODES = new Set<BlendMode>([
  'normal', 'pass through', 'darken', 'multiply', 'color burn', 'linear burn',
  'lighten', 'screen', 'color dodge', 'linear dodge', 'overlay', 'soft light',
  'hard light', 'difference', 'exclusion', 'subtract', 'divide',
])

function clamp(value: number) {
  return Math.min(1, Math.max(0, value))
}

function blendChannel(mode: BlendMode, backdrop: number, source: number) {
  switch (mode) {
    case 'darken': return Math.min(backdrop, source)
    case 'multiply': return backdrop * source
    case 'color burn': return source <= 0 ? 0 : 1 - Math.min(1, (1 - backdrop) / source)
    case 'linear burn': return Math.max(0, backdrop + source - 1)
    case 'lighten': return Math.max(backdrop, source)
    case 'screen': return backdrop + source - backdrop * source
    case 'color dodge': return source >= 1 ? 1 : Math.min(1, backdrop / (1 - source))
    case 'linear dodge': return Math.min(1, backdrop + source)
    case 'overlay': return backdrop <= 0.5 ? 2 * backdrop * source : 1 - 2 * (1 - backdrop) * (1 - source)
    case 'soft light': {
      if (source <= 0.5) return backdrop - (1 - 2 * source) * backdrop * (1 - backdrop)
      const d = backdrop <= 0.25 ? ((16 * backdrop - 12) * backdrop + 4) * backdrop : Math.sqrt(backdrop)
      return backdrop + (2 * source - 1) * (d - backdrop)
    }
    case 'hard light': return source <= 0.5 ? 2 * backdrop * source : 1 - 2 * (1 - backdrop) * (1 - source)
    case 'difference': return Math.abs(backdrop - source)
    case 'exclusion': return backdrop + source - 2 * backdrop * source
    case 'subtract': return Math.max(0, backdrop - source)
    case 'divide': return source <= 0 ? 1 : Math.min(1, backdrop / source)
    default: return source
  }
}

function dataMax(data: PixelData['data']) {
  return data instanceof Uint16Array ? 65535 : 255
}

export function getCompositeBounds(
  nodes: LayerNode[],
  mode: CompositeBoundsMode,
  documentWidth: number,
  documentHeight: number,
): CompositeBounds {
  if (mode === 'document') {
    return { left: 0, top: 0, right: documentWidth, bottom: documentHeight, width: documentWidth, height: documentHeight }
  }
  const layers = nodes.filter((node) => node.layer.imageData)
  if (!layers.length) return { left: 0, top: 0, right: 0, bottom: 0, width: 0, height: 0 }
  const left = Math.min(...layers.map((node) => node.layer.left ?? 0))
  const top = Math.min(...layers.map((node) => node.layer.top ?? 0))
  const right = Math.max(...layers.map((node) => (node.layer.left ?? 0) + (node.layer.imageData?.width ?? 0)))
  const bottom = Math.max(...layers.map((node) => (node.layer.top ?? 0) + (node.layer.imageData?.height ?? 0)))
  return { left, top, right, bottom, width: Math.max(0, right - left), height: Math.max(0, bottom - top) }
}

function fillBackground(data: Uint8ClampedArray | Uint16Array, background: CompositeBackground) {
  if (background === 'transparent') return
  const max = data instanceof Uint16Array ? 65535 : 255
  const channel = background === 'white' ? max : 0
  for (let index = 0; index < data.length; index += 4) {
    data[index] = channel
    data[index + 1] = channel
    data[index + 2] = channel
    data[index + 3] = max
  }
}

function compositeLayer(
  destination: Uint8ClampedArray | Uint16Array,
  width: number,
  height: number,
  originLeft: number,
  originTop: number,
  source: PixelData,
  sourceLeft: number,
  sourceTop: number,
  blendMode: BlendMode,
) {
  const destinationMax = destination instanceof Uint16Array ? 65535 : 255
  const sourceMax = dataMax(source.data)
  for (let y = 0; y < source.height; y += 1) {
    const targetY = sourceTop + y - originTop
    if (targetY < 0 || targetY >= height) continue
    for (let x = 0; x < source.width; x += 1) {
      const targetX = sourceLeft + x - originLeft
      if (targetX < 0 || targetX >= width) continue
      const sourceIndex = (y * source.width + x) * 4
      const destinationIndex = (targetY * width + targetX) * 4
      const sourceAlpha = Number(source.data[sourceIndex + 3]) / sourceMax
      if (sourceAlpha <= 0) continue
      const backdropAlpha = Number(destination[destinationIndex + 3]) / destinationMax
      const outputAlpha = sourceAlpha + backdropAlpha - sourceAlpha * backdropAlpha
      for (let channel = 0; channel < 3; channel += 1) {
        const sourceColor = Number(source.data[sourceIndex + channel]) / sourceMax
        const backdropColor = Number(destination[destinationIndex + channel]) / destinationMax
        const blended = blendChannel(blendMode, backdropColor, sourceColor)
        const premultiplied =
          (1 - sourceAlpha) * backdropColor * backdropAlpha +
          (1 - backdropAlpha) * sourceColor * sourceAlpha +
          sourceAlpha * backdropAlpha * blended
        destination[destinationIndex + channel] = Math.round(clamp(outputAlpha > 0 ? premultiplied / outputAlpha : 0) * destinationMax)
      }
      destination[destinationIndex + 3] = Math.round(clamp(outputAlpha) * destinationMax)
    }
  }
}

export function composeLayers(nodesTopToBottom: LayerNode[], options: CompositeOptions): CompositeResult {
  const bounds = getCompositeBounds(nodesTopToBottom, options.bounds, options.documentWidth, options.documentHeight)
  if (!bounds.width || !bounds.height) throw new Error('選択範囲に書き出せるピクセルがありません。')
  const is16Bit = nodesTopToBottom.some((node) => node.layer.imageData?.data instanceof Uint16Array)
  const data = is16Bit
    ? new Uint16Array(bounds.width * bounds.height * 4)
    : new Uint8ClampedArray(bounds.width * bounds.height * 4)
  fillBackground(data, options.background)
  const approximated = new Set<BlendMode>()

  for (const node of [...nodesTopToBottom].reverse()) {
    const pixels = renderLayerPixels(node.layer, options.applyMask, options.applyOpacity)
    if (!pixels) continue
    const requestedMode = node.layer.blendMode ?? 'normal'
    let blendMode: BlendMode = options.applyBlendModes ? requestedMode : 'normal'
    if (!SUPPORTED_BLEND_MODES.has(blendMode)) {
      approximated.add(blendMode)
      blendMode = 'normal'
    }
    compositeLayer(
      data,
      bounds.width,
      bounds.height,
      bounds.left,
      bounds.top,
      pixels,
      node.layer.left ?? 0,
      node.layer.top ?? 0,
      blendMode,
    )
  }

  return { image: { width: bounds.width, height: bounds.height, data }, bounds, approximatedBlendModes: [...approximated] }
}
